/**
 * useRoadmap Hook
 *
 * Feature-specific hook for the Roadmap (Ideas) page.
 *
 * Architecture: Page → Hook → UseCase → Service
 *
 * Mirrors Flutter's `RoadmapScreenState._getData` + `_scrollListener` +
 * `_likeTapped` / `_dislikeTapped` + `_onRoadmapItemLikeChange`
 * (lib/presentation/screens/roadmap/roadmap_screen.dart:402-472,128-135,189-394).
 *
 * Responsibilities:
 * - Initial fetch on init: the paginated list (when opened normally) or a
 *   single item (when opened from a notification deep link, where
 *   `roadmapItemInfo` is the payload the page reads from `location.state`).
 *   Mirrors Flutter's `widget.itemInfo == null` branch split in `_getData`
 *   (roadmap_screen.dart:417-425).
 * - Surfacing the deep-link auto-open *intent* after the single-item fetch
 *   (Flutter's `widget.openComments` / `widget.openLikes` branch,
 *   roadmap_screen.dart:457-471): the hook computes WHAT to open
 *   (`pendingDeepLinkOpen` — `likes` with `count = likedType ? item.likes :
 *   item.dislikes`, or `comments`) but never opens the sheet itself — it has no
 *   page-minted `screenKey`. The page consumes the intent and performs the
 *   actual `RoadmapItemLikesSheet.show(...)` / comments open.
 * - Infinite-scroll pagination via a `PageCursor` ref (the web equivalent of
 *   Flutter's `_lastFetchedItem` DocumentSnapshot), guarded on
 *   `!hasMore || isLoadingMore` — Flutter's `!_hasMore || _isFetching`
 *   guard (roadmap_screen.dart:403).
 * - The non-owner like/dislike toggle (`toggleReaction`): the `isSendingLike`
 *   in-flight guard, then `saveRoadmapItemLikeUseCase.execute(...)` — an
 *   `execute`-only use case that writes the optimistic local-storage record,
 *   calls the `saveRoadmapItemLike` Cloud Function, and reverts the storage
 *   write on failure. The hook applies the eager-optimistic **count** delta
 *   (which lives on the in-memory item, not in storage) and reverts it on
 *   `!success` or throw; the **icon tint** is NOT threaded here — it flows from
 *   the optimistic storage write firing the `ROADMAP_LIKES`-prefix storage
 *   listener below. This is the web equivalent of Flutter's `_likeTapped` /
 *   `_dislikeTapped` non-owner path (roadmap_screen.dart:205-271 / :289-350),
 *   which mutates `item.likes` before the await, plus the reactive re-tint from
 *   the Hive box watch (`_onRoadmapItemLikeChange`, :380-394). Web additionally
 *   reverts the in-memory count on failure (Flutter reverts only storage,
 *   :256-268 — a Flutter bug). The owner short-circuit (open the likes sheet)
 *   lives in the page.
 * - The reactive like-tint refresh (`_onRoadmapItemLikeChange`, :380-394): a
 *   `ROADMAP_LIKES`-prefix storage listener (`loadInitialItems: false` — the
 *   items + their initial `likeEntity` already come from the fetch use cases;
 *   this is purely a live-update channel) maps each emitted
 *   `RoadmapItemLikeStorage` through `RoadmapItemLikeRemapper.fromStorage` and
 *   sets the matching item's `likeEntity`, re-tinting the icon on any like
 *   change — the web port of Flutter's Hive box watch. A first-ever-reaction
 *   failure reverts via `storage.remove`, which the listener surfaces on its
 *   `deletedKeys` (not `items`) channel; a sibling effect consumes that channel
 *   and clears the matching item back to `{ liked: false, disliked: false }`
 *   (with the count delta) so the failed first reaction reverts both tint and
 *   count, not just the had-previous path.
 * - Surface loading / loading-more / hasMore / error to the UI, with
 *   `appLogger.error` on failure (matching `useSavedContent`).
 * - The post-add reload: `prependLatestUserItem(userId)` re-fetches the user's
 *   most recently created item and inserts it at the top of the list — Flutter's
 *   `_onAddRoadmapItemTapped` `getLatestUserRoadmapItem()` → `_items.insert(0, …)`
 *   (roadmap_screen.dart:145-147). The page calls it on `ROADMAP_ITEM_ADDED`.
 *
 * The comments sheet IS ported (Task 12/13); the page opens it from the card's
 * comment action and mirrors the new comment count back onto the in-memory item
 * via `patchItemComments` on `ROADMAP_ITEM_COMMENTS_UPDATED` (the web equivalent
 * of Flutter's shared-entity reference, roadmap_screen.dart:663-671).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { RoadmapItemEntity } from '@/domain/entities/roadmap/roadmapItemEntity';
import type { RoadmapItemInfoEntity } from '@/domain/entities/info/roadmapItemInfoEntity';
import type { RoadmapItemLikeEntity } from '@/domain/entities/roadmap/roadmapItemLikeEntity';
import type { RoadmapItemLikeStorage } from '@/domain/entities/secureStorage/secureStorageEntities';
import type { PageCursor } from '@/data/types/json';
import { fetchRoadmapUseCase } from '@/domain/usecases/roadmap/fetchRoadmapUseCase';
import { fetchNotificationRoadmapItemUseCase } from '@/domain/usecases/roadmap/fetchNotificationRoadmapItemUseCase';
import { saveRoadmapItemLikeUseCase } from '@/domain/usecases/roadmap/saveRoadmapItemLikeUseCase';
import { fetchLatestUserRoadmapItemUseCase } from '@/domain/usecases/roadmap/fetchLatestUserRoadmapItemUseCase';
import { RoadmapItemLikeRemapper } from '@/domain/remappers/roadmapItemLike/roadmapItemLikeRemapper';
import { RoadmapDeepLinkOpenKind } from '@/domain/enums/roadmap/roadmapDeepLinkOpenKind';
import { appLogger } from '@/domain/services/analytics/appLogger';
import { useListenToSecureStorageUpdatesByPrefix } from '@/data/storage/local/secureStorageWebCrypto';
import { SECURE_STORAGE_KEYS } from '@/domain/entities/secureStorage/secureStorageKeys';
import { useLocale } from '@/presentation/contexts/LocaleContext';

/**
 * One-shot deep-link auto-open intent surfaced by the hook after the
 * notification single-item fetch — the web split of Flutter's
 * `widget.openComments` / `widget.openLikes` branch (roadmap_screen.dart:457-471).
 * The hook computes this; the PAGE performs the actual sheet open (it owns the
 * `screenKey`). `count` is already resolved as Flutter does
 * (`likedType ? item.likes : item.dislikes`, roadmap_screen.dart:466) so the
 * page passes it straight through.
 */
export type PendingDeepLinkOpen =
  | { kind: RoadmapDeepLinkOpenKind.likes; roadmapItemId: string; likedType: boolean; count: number }
  | { kind: RoadmapDeepLinkOpenKind.comments; roadmapItemId: string };

export interface UseRoadmapResult {
  items: RoadmapItemEntity[];
  isLoading: boolean;
  isLoadingMore: boolean;
  hasMore: boolean;
  error: string | null;
  /**
   * The deep-link auto-open intent, set once after the notification single-item
   * fetch and cleared once the page consumes it via `consumePendingDeepLinkOpen`.
   * `null` for a normal paginated open or a deep link with no auto-open flag.
   */
  pendingDeepLinkOpen: PendingDeepLinkOpen | null;
  /** Clear `pendingDeepLinkOpen` after the page has opened the sheet (one-shot). */
  consumePendingDeepLinkOpen: () => void;
  init: () => Promise<void>;
  loadMore: () => Promise<void>;
  toggleReaction: (
    itemId: string,
    liked: boolean,
    username: string,
    currentUserId: string,
  ) => Promise<void>;
  prependLatestUserItem: (userId: string) => Promise<void>;
  patchItemComments: (roadmapItemId: string, comments: number) => void;
}

/**
 * The in-memory count delta implied by a like-state transition — the value the
 * storage listener applies to the matching item's `likes` / `dislikes` so the
 * count tracks the tint. This is the same arithmetic the use case's
 * `computeToggle` encodes in `request.likesIncrement` / `dislikesIncrement`
 * (each reaction flag flips ±1; switching reactions cross-clears the opposite),
 * re-expressed as the diff between the prior and next lean like-state. Counts
 * are not stored, so they cannot flow through the storage value itself — but the
 * delta is fully determined by the transition the listener observes. Each emit
 * composes against the item's current `likeEntity`, so an optimistic write and
 * its later revert net to zero.
 */
const computeLikeCountDeltas = (
  prev: RoadmapItemEntity['likeEntity'],
  next: RoadmapItemLikeEntity,
): { likesDelta: number; dislikesDelta: number } => {
  const prevLiked = prev?.liked ?? false;
  const prevDisliked = prev?.disliked ?? false;
  return {
    likesDelta: Number(next.liked) - Number(prevLiked),
    dislikesDelta: Number(next.disliked) - Number(prevDisliked),
  };
};

export const useRoadmap = (roadmapItemInfo?: RoadmapItemInfoEntity): UseRoadmapResult => {
  const { l10n } = useLocale();

  const [items, setItems] = useState<RoadmapItemEntity[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Deep-link auto-open intent (Flutter's `widget.openComments`/`widget.openLikes`
  // branch, roadmap_screen.dart:457-471). Set once after the notification
  // single-item fetch; the page consumes + clears it. The hook never opens the
  // sheet — it has no page-minted `screenKey`.
  const [pendingDeepLinkOpen, setPendingDeepLinkOpen] = useState<PendingDeepLinkOpen | null>(null);

  // Pagination cursor — the web equivalent of Flutter's `_lastFetchedItem`
  // DocumentSnapshot. Opaque (`PageCursor`); forwarded straight back to the
  // use case for the next page.
  const cursorRef = useRef<PageCursor | undefined>(undefined);
  // Guard against re-entrant loadMore calls fired by overlapping scroll
  // events, like `useSavedContent`'s `isLoadingMoreRef`.
  const isLoadingMoreRef = useRef(false);
  // Single-item mode: set true when the page is opened from a notification
  // deep link (Flutter's `widget.itemInfo != null`). In this mode the list is
  // a single item and pagination is disabled.
  const isSingleItemModeRef = useRef(roadmapItemInfo != null);

  // Reactive like-tint channel — the web port of Flutter's Hive box watch
  // (`_roadmapItemLikeBox.watch().listen(_onRoadmapItemLikeChange)`,
  // roadmap_screen.dart:374-394). `loadInitialItems: false`: the items and
  // their initial `likeEntity` already arrive through the fetch use cases, so
  // this is purely a live-update channel. The discriminator is the stored
  // record's `roadmapItemId`, which equals the storage-key suffix (`item.id`)
  // for these records. The optimistic write inside
  // `saveRoadmapItemLikeUseCase.execute` (and its had-previous revert on
  // failure) fires the `items` channel here, re-tinting the matching item; the
  // first-ever-reaction failure revert is a `remove`, which surfaces on
  // `deletedKeys` instead (see the delete effect below).
  const { items: likeStorageUpdates, deletedKeys: likeStorageDeletes } =
    useListenToSecureStorageUpdatesByPrefix<RoadmapItemLikeStorage>(
      SECURE_STORAGE_KEYS.ROADMAP_LIKES,
      (storage) => storage.roadmapItemId,
      false,
    );

  // Cursor into the append-only `deletedKeys` stream (see the delete effect
  // below). `deletedKeys` only ever grows, so each run processes just the new
  // tail past this index — re-running the effect on an unrelated re-render
  // re-applies nothing.
  const processedDeletesRef = useRef(0);

  const init = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    setError(null);

    try {
      // Notification deep-link branch: fetch only the single referenced item
      // and disable pagination — mirrors Flutter's `itemInfo != null` branch
      // (roadmap_screen.dart:423-425), which returns a one-item response and
      // leaves `_hasMore` false.
      if (roadmapItemInfo != null) {
        isSingleItemModeRef.current = true;
        const result = await fetchNotificationRoadmapItemUseCase.execute(
          roadmapItemInfo.roadmapItemId,
        );
        setItems(result);
        setHasMore(false);

        // Surface the auto-open INTENT only — the page performs the open
        // (it owns the `screenKey`). Mirrors Flutter's post-fetch branch
        // (roadmap_screen.dart:457-471): `openComments` wins over `openLikes`,
        // and the likes count is `likedType ? item.likes : item.dislikes`
        // (:466). Guard on a resolved item so a missing deep-linked item opens
        // nothing.
        const item = result[0];
        if (item != null) {
          if (roadmapItemInfo.openComments === true) {
            setPendingDeepLinkOpen({
              kind: RoadmapDeepLinkOpenKind.comments,
              roadmapItemId: item.id,
            });
          } else if (roadmapItemInfo.openLikes === true) {
            const likedType = roadmapItemInfo.likedType === true;
            setPendingDeepLinkOpen({
              kind: RoadmapDeepLinkOpenKind.likes,
              roadmapItemId: item.id,
              likedType,
              count: likedType ? item.likes : item.dislikes,
            });
          }
        }
        return;
      }

      // Normal paginated-list branch (roadmap_screen.dart:419-422): first page.
      isSingleItemModeRef.current = false;
      cursorRef.current = undefined;
      const result = await fetchRoadmapUseCase.execute();
      cursorRef.current = result.cursor;
      setItems(result.items);
      setHasMore(result.hasMore);
    } catch (err) {
      appLogger.error('Error fetching roadmap', 'useRoadmap.init', { error: err as Error });
      setError(l10n.roadmap.errorLoading);
      setItems([]);
      setHasMore(false);
    } finally {
      setIsLoading(false);
    }
  }, [roadmapItemInfo, l10n]);

  // One-shot clear so the deep-link sheet opens exactly once (the page calls
  // this right after performing the open).
  const consumePendingDeepLinkOpen = useCallback((): void => {
    setPendingDeepLinkOpen(null);
  }, []);

  // Reactive re-tint + count — the direct web port of Flutter's
  // `_onRoadmapItemLikeChange` (roadmap_screen.dart:380-394): on each
  // like-storage write/revert under the `ROADMAP_LIKES` prefix, find the
  // matching item and set its `likeEntity`. The prefix listener's `items` array
  // carries the latest stored value per `roadmapItemId` (it dedupes in place on
  // `getItemKey`).
  //
  // The optimistic storage write inside `saveRoadmapItemLikeUseCase.execute`
  // fires this event BEFORE the Cloud Function await (secureStorageWebCrypto
  // emits at the IndexedDB `onsuccess`), so the tint AND the count update on tap
  // — preserving the round-1 eager-optimistic behaviour. On `!success` the use
  // case reverts the storage write, which fires this listener again with the
  // prior state, automatically reverting both the tint and the count.
  //
  // The count delta cannot be read from storage (counts live on the in-memory
  // item), but it is fully determined by the like-state TRANSITION the listener
  // observes — exactly the `request.likesIncrement` / `dislikesIncrement` the
  // use case computes — so it is derived here from `prev.likeEntity` →
  // `nextLikeEntity` (see `computeLikeCountDeltas`). Each emit composes against
  // the item's current in-memory `likeEntity`, so consecutive write+revert
  // events net to zero. Re-running on an unrelated re-render is a no-op: the
  // like-state is unchanged, so `computeLikeCountDeltas` returns `0/0` and the
  // `changed` guard skips the `setItems`.
  useEffect(() => {
    if (likeStorageUpdates.length === 0) {
      return;
    }

    setItems((prev) => {
      let changed = false;
      const next = prev.map((candidate) => {
        const update = likeStorageUpdates.find(
          (storage) => storage.roadmapItemId === candidate.id,
        );
        if (update == null) {
          return candidate;
        }
        const nextLikeEntity = RoadmapItemLikeRemapper.fromStorage(update);
        const { likesDelta, dislikesDelta } = computeLikeCountDeltas(
          candidate.likeEntity,
          nextLikeEntity,
        );
        if (
          likesDelta === 0 &&
          dislikesDelta === 0 &&
          candidate.likeEntity?.liked === nextLikeEntity.liked &&
          candidate.likeEntity?.disliked === nextLikeEntity.disliked
        ) {
          return candidate;
        }
        changed = true;
        return {
          ...candidate,
          likeEntity: nextLikeEntity,
          likes: candidate.likes + likesDelta,
          dislikes: candidate.dislikes + dislikesDelta,
        };
      });
      return changed ? next : prev;
    });
  }, [likeStorageUpdates]);

  // Revert channel for the FIRST-EVER-reaction failure path. When a user reacts
  // to an item they had never reacted to and the Cloud Function returns
  // `success = false`, the use case reverts by `storage.remove(key)` (there was
  // no `previous` to restore). That `remove` emits `{ value: null }`, which the
  // prefix listener surfaces NOT on `items` (it filters the entity out) but on
  // `deletedKeys` — so the `items`-channel effect above would never see it and
  // the optimistic tint/count would stay stuck (+1, tinted) until a refetch.
  //
  // This effect closes that gap: each delete key is the storage-key suffix,
  // which equals `roadmapItemId === item.id`. On a delete for a roadmap item,
  // reset that item to the cleared like-state (`{ liked: false, disliked: false
  // }`) and derive the count delta the same way the `items`-channel effect does
  // (via `computeLikeCountDeltas` against the item's current `likeEntity`), so
  // the failed first reaction's tint AND count both revert. (The had-previous
  // failure path reverts through the `items` channel — the use case re-writes
  // the previous record, which re-emits a real value.)
  //
  // `deletedKeys` is append-only and never shrinks, so we process only the new
  // tail past `processedDeletesRef` to apply each delete exactly once (an
  // item may be deleted again after a re-tap-and-fail, so we cursor by index
  // rather than dedupe by key).
  useEffect(() => {
    if (likeStorageDeletes.length <= processedDeletesRef.current) {
      return;
    }

    const newlyDeletedIds = new Set(
      likeStorageDeletes.slice(processedDeletesRef.current),
    );
    processedDeletesRef.current = likeStorageDeletes.length;

    setItems((prev) => {
      let changed = false;
      const next = prev.map((candidate) => {
        if (!newlyDeletedIds.has(candidate.id)) {
          return candidate;
        }
        const clearedLikeEntity: RoadmapItemLikeEntity = {
          roadmapItemId: candidate.id,
          liked: false,
          disliked: false,
        };
        const { likesDelta, dislikesDelta } = computeLikeCountDeltas(
          candidate.likeEntity,
          clearedLikeEntity,
        );
        if (
          likesDelta === 0 &&
          dislikesDelta === 0 &&
          candidate.likeEntity?.liked === false &&
          candidate.likeEntity?.disliked === false
        ) {
          return candidate;
        }
        changed = true;
        return {
          ...candidate,
          likeEntity: clearedLikeEntity,
          likes: candidate.likes + likesDelta,
          dislikes: candidate.dislikes + dislikesDelta,
        };
      });
      return changed ? next : prev;
    });
  }, [likeStorageDeletes]);

  const loadMore = useCallback(async (): Promise<void> => {
    // Guard parity with Flutter's `!_hasMore || _isFetching` (roadmap_screen.dart:403),
    // plus the single-item-mode short-circuit (no pagination when opened from
    // a notification).
    if (isSingleItemModeRef.current || isLoadingMoreRef.current || !hasMore) {
      return;
    }

    setIsLoadingMore(true);
    isLoadingMoreRef.current = true;
    setError(null);

    try {
      const result = await fetchRoadmapUseCase.execute(cursorRef.current);
      cursorRef.current = result.cursor;
      setItems((prev) => [...prev, ...result.items]);
      setHasMore(result.hasMore);
    } catch (err) {
      appLogger.error('Error loading more roadmap items', 'useRoadmap.loadMore', { error: err as Error });
      setError(l10n.roadmap.loadMoreFailed);
    } finally {
      setIsLoadingMore(false);
      isLoadingMoreRef.current = false;
    }
  }, [hasMore, l10n]);

  // Non-owner like/dislike toggle. Web equivalent of Flutter's `_likeTapped` /
  // `_dislikeTapped` non-owner path (roadmap_screen.dart:205-271 / :289-350).
  // The owner short-circuit (open the likes sheet) is handled by the page
  // before this is called.
  //
  // The use case is `execute`-only: it writes the optimistic local-storage
  // record, calls the Cloud Function, and reverts the storage write on failure.
  // BOTH the eager-optimistic tint AND the count are sourced from the
  // `ROADMAP_LIKES` storage listener above (the web port of Flutter's Hive box
  // watch), not threaded here — the optimistic write fires the listener before
  // the Cloud Function await, and the failure revert fires it again to undo
  // both. So `toggleReaction` only owns the in-flight (`isSendingLike`) guard.
  //
  // `liked` is a boolean — `true` = like, `false` = dislike (matching the
  // use-case + read-side signatures), NOT a `'like' | 'dislike'` string union.
  const toggleReaction = useCallback(
    async (
      itemId: string,
      liked: boolean,
      username: string,
      currentUserId: string,
    ): Promise<void> => {
      const item = items.find((candidate) => candidate.id === itemId);
      if (item == null) {
        return;
      }

      // In-flight guard — Flutter's `if (item.isSendingLike) return;`
      // (roadmap_screen.dart:205-207 / :289-291).
      if (item.isSendingLike) {
        return;
      }

      // Mark the item in-flight before the use case runs, so a re-tap is
      // guarded while the Cloud Function is pending (Flutter's
      // `item.isSendingLike = true`, :209/:293).
      setItems((prev) =>
        prev.map((candidate) =>
          candidate.id === itemId
            ? { ...candidate, isSendingLike: true }
            : candidate,
        ),
      );

      try {
        // `execute` writes the optimistic storage record (firing the listener's
        // re-tint + count update), calls the Cloud Function, and reverts the
        // storage write on failure (which fires the listener again to undo the
        // optimistic tint + count). Nothing to thread back here.
        await saveRoadmapItemLikeUseCase.execute({
          item,
          liked,
          username,
          currentUserId,
        });
      } catch (err) {
        appLogger.error('Error toggling roadmap item reaction', 'useRoadmap.toggleReaction', {
          error: err as Error,
        });
      } finally {
        // Clear the in-flight flag (Flutter's `item.isSendingLike = false`,
        // :270/:350). The tint + count are owned by the storage listener.
        setItems((prev) =>
          prev.map((candidate) =>
            candidate.id === itemId
              ? { ...candidate, isSendingLike: false }
              : candidate,
          ),
        );
      }
    },
    [items],
  );

  // Post-add reload: re-fetch the user's most recently created item and insert
  // it at the top of the list — Flutter's `_onAddRoadmapItemTapped`
  // `getLatestUserRoadmapItem()` → `_items.insert(0, item.toEntity())`
  // (roadmap_screen.dart:145-147). Called by the page on `ROADMAP_ITEM_ADDED`.
  //
  // The page supplies the user id (it owns the auth-gate selector per the
  // page/hook split). Guards against a double-prepend on a re-emit by skipping
  // when the returned item id is already at the head of the list.
  //
  // Web deviation (per task plan): Flutter shows a brief full-screen loader
  // (`_isFetchingAddedRoadmapItem`) during this single-item refetch. The web
  // omits that transient overlay — the prepend is one fast read and the list
  // updates in place.
  const prependLatestUserItem = useCallback(async (userId: string): Promise<void> => {
    if (!userId) {
      return;
    }

    try {
      const item = await fetchLatestUserRoadmapItemUseCase.execute(userId);
      if (item == null) {
        return;
      }
      setItems((prev) => {
        // Guard against double-prepend on a re-emit: skip if this exact item is
        // already present.
        if (prev.some((existing) => existing.id === item.id)) {
          return prev;
        }
        return [item, ...prev];
      });
    } catch (err) {
      appLogger.error(
        'Error fetching latest user roadmap item',
        'useRoadmap.prependLatestUserItem',
        { error: err as Error },
      );
    }
  }, []);

  // Mirror a roadmap item's new comment count onto the in-memory list when the
  // comments sheet emits `ROADMAP_ITEM_COMMENTS_UPDATED` (add/reply). The web
  // equivalent of Flutter's shared-entity reference, where the sheet bumps
  // `roadmapItemEntity.comments` and the card reflects it directly
  // (roadmap_screen.dart:663-671). Immutable replacement so the matching card
  // re-renders; a no-op when the item is not in the current list.
  const patchItemComments = useCallback((roadmapItemId: string, comments: number): void => {
    setItems((prev) =>
      prev.map((existing) =>
        existing.id === roadmapItemId ? { ...existing, comments } : existing,
      ),
    );
  }, []);

  return {
    items,
    isLoading,
    isLoadingMore,
    hasMore,
    error,
    pendingDeepLinkOpen,
    consumePendingDeepLinkOpen,
    init,
    loadMore,
    toggleReaction,
    prependLatestUserItem,
    patchItemComments,
  };
};
